import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const IntelligenceSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section 
      id="intelligence" 
      className="py-24 md:py-32 relative" 
      ref={ref} 
      data-testid="intelligence-section"
      style={{
        background: 'linear-gradient(180deg, #050505 0%, #0a1225 50%, #050505 100%)'
      }}
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light tracking-[0.2em] text-white mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Open Intelligence
          </h2>
          <div className="w-24 h-[1px] bg-[#c9a227]/50 mx-auto" />
        </motion.div>

        {/* The Open Intelligence Vault */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12 text-center"
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-[#c9a227] mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            The Open Intelligence Vault™
          </h3>
          <p 
            className="text-lg md:text-xl italic text-[#c9a227]/80 mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            "Intelligence released for the advancement of humanity."
          </p>
        </motion.div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="space-y-6 text-white/70 leading-relaxed">
            <p>
              The Open Intelligence Vault™ is a public intelligence layer of Celestial Minds™, created to release select, high-impact ideas openly—without equity claims, licensing demands, or proprietary constraints.
            </p>
            <p>
              It exists in recognition that not all intelligence must be owned to be valuable, not all ideas require venture capital to matter, and not all progress should wait for permission.
            </p>
            <p>
              These ideas are fully considered, technically plausible, and intentionally released with one purpose only: that those with the means, capability, and conviction bring them to life in service of human safety, dignity, and advancement.
            </p>
            <p>
              Celestial Minds believes intelligence compounds when shared wisely. Some ideas should be protected, some commercialized, and some simply released. The Open Intelligence Vault represents the latter—a statement of confidence rather than concession.
            </p>
            <p>
              The ideas within are offered in good faith as intellectual contributions, not mandates, and are meant to be explored and responsibly implemented by those qualified to do so.
            </p>
            <p className="text-[#c9a227] italic">
              To engineers, researchers, manufacturers, policymakers, and builders of the future: if an idea here resonates, take it forward. Build responsibly, think deeply, and act ethically. Recognition is welcome but never expected. Impact matters more than attribution.
            </p>
          </div>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-12" />

        {/* Open Intelligence Release #001 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h3 
            className="text-xl font-light tracking-wider text-[#c9a227] mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Open Intelligence Release #001 / January 2026
          </h3>
          <div className="space-y-4 text-white/70 leading-relaxed">
            <p>
              This idea was not developed as a product, nor is it intended to be owned, patented, or commercialised by Celestial Minds™. It exists because a clear gap was identified—one with potential implications for human safety—and it was considered important to define the idea properly and make it available openly.
            </p>
            <p>
              This release introduces <span className="text-white">AI-Adaptive Collision-Responsive Seating</span>, a concept in which vehicle seats do not remain passive during a collision, but instead respond intelligently by repositioning occupants to help reduce injury severity when an impact cannot be avoided.
            </p>
            <p>
              The concept is shared freely, without obligation or expectation, in the hope that those with the appropriate expertise and resources may choose to explore, test, and implement it responsibly.
            </p>
            <p className="text-[#c9a227] italic text-lg mt-6">
              "Progress does not always require permission. Sometimes it simply requires clarity."
            </p>
          </div>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-12" />

        {/* ACRSS System Overview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h3 
            className="text-xl font-light tracking-wider text-white mb-2"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            AI-Adaptive Collision-Responsive Seating System (ACRSS)
          </h3>
          <p className="text-[#c9a227] text-sm mb-6">Canonical Description</p>
          
          <div className="space-y-4 text-white/70 leading-relaxed">
            <p>
              The AI-Adaptive Collision-Responsive Seating System (ACRSS) is an automotive safety concept in which vehicle seats function as active biomechanical positioning systems during unavoidable collisions, rather than remaining static structures.
            </p>
            <p>
              The system continuously evaluates vehicle dynamics and environmental inputs in real time, while remaining fully inactive during normal driving conditions. It activates only when a collision is determined to be physically unavoidable, at which point it carries out precisely calculated micro-adjustments to seat position and restraint coordination to improve occupant survivability.
            </p>
            <p>
              In this context, the seat operates as an active safety interface, working in coordination with airbags, seatbelts, and the vehicle structure during the impact phase itself.
            </p>
          </div>
        </motion.div>

        {/* Problem Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h4 className="text-lg text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>Problem Statement</h4>
          <div className="space-y-4 text-white/70 leading-relaxed">
            <p>Modern vehicle safety architecture primarily focuses on energy dissipation through vehicle structure, restraint systems such as airbags and seatbelts, and post-impact injury mitigation.</p>
            <p>However, occupant spatial positioning at the moment of impact remains largely fixed, despite its significant influence on injury outcomes.</p>
            <p className="text-white/90 font-medium">This creates a systemic gap:</p>
            <p className="text-white/90 italic">The human body is often not optimally positioned to benefit fully from the protection that existing safety systems are capable of providing.</p>
            <p>ACRSS addresses this gap by introducing dynamic, last-moment occupant repositioning to improve biomechanical alignment during impact.</p>
          </div>
        </motion.div>

        {/* System Overview Sections */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="space-y-8"
        >
          {/* 1. Continuous Passive Assessment */}
          <div className="pl-6 border-l-2 border-[#c9a227]/30">
            <h4 className="text-lg text-white mb-3" style={{ fontFamily: "'Cinzel', serif" }}>1. Continuous Passive Assessment</h4>
            <p className="text-white/70 mb-3">The system continuously models:</p>
            <ul className="space-y-1 text-white/60 text-sm">
              <li>• Vehicle kinematics (speed, acceleration, yaw, braking)</li>
              <li>• Environmental threats (radar, LiDAR, camera fusion)</li>
              <li>• Object proximity and collision vectors</li>
              <li>• Road friction and grip conditions</li>
              <li>• Occupant posture and seat configuration</li>
            </ul>
            <p className="text-white/70 mt-3 italic">No intervention occurs during normal driving.</p>
          </div>

          {/* 2. Collision Inevitability Determination */}
          <div className="pl-6 border-l-2 border-[#c9a227]/30">
            <h4 className="text-lg text-white mb-3" style={{ fontFamily: "'Cinzel', serif" }}>2. Collision Inevitability Determination</h4>
            <p className="text-white/70">When predictive models determine that a collision cannot be avoided within physical constraints and time-to-impact enters a critical millisecond window, the system transitions from passive monitoring to active collision-phase response.</p>
          </div>

          {/* 3. Dynamic Seat Repositioning */}
          <div className="pl-6 border-l-2 border-[#c9a227]/30">
            <h4 className="text-lg text-white mb-3" style={{ fontFamily: "'Cinzel', serif" }}>3. Dynamic Seat Repositioning</h4>
            <p className="text-white/70 mb-3">Based on collision direction and severity, the system may carry out controlled micro-adjustments such as:</p>
            <ul className="space-y-1 text-white/60 text-sm">
              <li>• Rearward seat movement in frontal impacts</li>
              <li>• Seat pitch adjustment to reduce spinal compression</li>
              <li>• Head restraint repositioning to mitigate cervical injury</li>
              <li>• Torso alignment to improve airbag interaction</li>
              <li>• Increased distance from intrusion zones where feasible</li>
            </ul>
            <p className="text-white/70 mt-3 text-sm">These adjustments are small in magnitude but biomechanically significant, typically measured in millimetres to centimetres and executed within milliseconds.</p>
          </div>

          {/* 4. Restraint System Synchronisation */}
          <div className="pl-6 border-l-2 border-[#c9a227]/30">
            <h4 className="text-lg text-white mb-3" style={{ fontFamily: "'Cinzel', serif" }}>4. Restraint System Synchronisation</h4>
            <p className="text-white/70">Seat repositioning is coordinated with dynamically modulated seatbelt tension, load-sharing strategies to reduce peak G-forces, and improved airbag timing and contact geometry. The seat, seatbelt, and airbag operate as a single, coordinated safety system.</p>
          </div>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent my-12" />

        {/* Release Status */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h4 className="text-lg text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>Release Status</h4>
          <p className="text-white/70 mb-4">This concept is released as:</p>
          <ul className="space-y-2 text-white/60 text-sm">
            <li>• A public safety intelligence contribution</li>
            <li>• A research and engineering exploration prompt</li>
            <li>• A design-level input for OEM and Tier-1 safety development</li>
          </ul>
          <p className="text-[#c9a227] font-medium mt-6">
            It is non-proprietary, non-patented, and non-commercialised.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default IntelligenceSection;
